import React, { useState, useRef } from "react";
import "./ProfilePage.css";
import SettingsPage from "./SettingsPage";
import WalletPage from "./WalletPage";
import { apiRequest, parseResponse } from "./utils/api";
import { useLanguage } from "./i18n/LanguageContext";

const ProfilePage = ({ onBack, onRequireLogin }) => {
  const { t } = useLanguage();
  // Get user data from localStorage
  const guestData = JSON.parse(localStorage.getItem("guestData") || "{}");
  const userData = guestData?.userData?.data || guestData?.userData || {};
  const selectedHotel = JSON.parse(
    localStorage.getItem("selectedHotel") || "{}",
  );

  const [view, setView] = useState("MENU"); // 'MENU', 'EDIT', 'SETTINGS', 'WALLET'
  const [fullName, setFullName] = useState(
    guestData.fullName || userData.fullName || "abcd",
  );
  const [email, setEmail] = useState(
    guestData.email || userData.email || "guest@hotelride.com",
  );
  const [phoneNumber, setPhoneNumber] = useState(
    guestData.mobileNumber || userData.mobileNumber || "1234",
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [profileImage, setProfileImage] = useState(
    userData.profileImage || guestData.profileImage || null,
  );
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const fileInputRef = useRef(null);

  const roomNumber = guestData.roomNumber || "402";
  const hotelName = selectedHotel.hotelName || "Hotel Radisson Blu";

  // Get initial letter for avatar
  const getInitial = () => {
    if (fullName && fullName.trim().length > 0) {
      return fullName.trim().charAt(0).toUpperCase();
    }
    return "A";
  };

  // Get user ID
  const getUserId = () => {
    const guestData = JSON.parse(localStorage.getItem("guestData") || "{}");
    const userData = guestData?.userData || {};
    const actualUserData = userData?.data || userData;
    return (
      actualUserData?.id ||
      actualUserData?.userId ||
      actualUserData?.customerId ||
      userData?.id ||
      userData?.userId ||
      userData?.customerId ||
      null
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage(null);

    try {
      const userId = getUserId();
      if (!userId) {
        throw new Error(
          "User ID not found. Please complete guest verification first.",
        );
      }

      // Call API to update user profile (phone number is not editable)
      const response = await apiRequest(`/users/${userId}`, {
        method: "PUT",
        body: JSON.stringify({
          fullName: fullName.trim(),
          email: email.trim(),
          // mobileNumber is not included as it's not editable
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to update profile");
      }

      // Update localStorage with new data
      const updatedGuestData = {
        ...guestData,
        fullName: fullName.trim(),
        email: email.trim(),
        mobileNumber: phoneNumber.trim(),
      };

      localStorage.setItem("guestData", JSON.stringify(updatedGuestData));

      setSaveMessage(t("Profile updated successfully!"));
      setTimeout(() => {
        setSaveMessage(null);
        setIsEditing(false);
        setView("MENU");
      }, 1500);
    } catch (error) {
      console.error("Error saving profile:", error);
      setSaveMessage(`Failed to save profile: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm(t("Are you sure you want to logout?"))) {
      localStorage.clear();
      window.location.reload();
    }
  };

  const handleBackAction = () => {
    if (view !== "MENU") {
      setView("MENU");
    } else {
      onBack();
    }
  };

  const handleImageClick = () => {
    // Show options for gallery or camera
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setSaveMessage("Please select an image file");
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setSaveMessage("Image size should be less than 5MB");
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    setIsUploadingImage(true);
    setSaveMessage(null);

    try {
      const userId = getUserId();
      if (!userId) {
        throw new Error(
          "User ID not found. Please complete guest verification first.",
        );
      }

      // Create FormData for multipart/form-data
      const formData = new FormData();
      formData.append("file", file);

      const response = await apiRequest(`/users/${userId}/upload-image`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to upload image");
      }

      const data = await parseResponse(response);

      // Update profile image state
      const imageUrl = data.imageUrl || data.url || URL.createObjectURL(file);
      setProfileImage(imageUrl);

      // Update localStorage
      const updatedGuestData = {
        ...guestData,
        profileImage: imageUrl,
      };
      localStorage.setItem("guestData", JSON.stringify(updatedGuestData));

      setSaveMessage(t("Profile image updated successfully!"));
      setTimeout(() => setSaveMessage(null), 2000);
    } catch (error) {
      console.error("Error uploading image:", error);
      setSaveMessage(`Failed to upload image: ${error.message}`);
      setTimeout(() => setSaveMessage(null), 3000);
    } finally {
      setIsUploadingImage(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  if (view === "SETTINGS") {
    return <SettingsPage onBack={() => setView("MENU")} />;
  }

  if (view === "WALLET") {
    return <WalletPage onBack={() => setView("MENU")} onRequireLogin={onRequireLogin} />;
  }

  const renderProfileField = (label, value, onChange, options = {}) => (
    <div className="form-group-minimal">
      <label>{label}</label>
      {isEditing ? (
        <input
          type={options.type || "text"}
          value={value}
          onChange={onChange}
          readOnly={options.readOnly}
          disabled={options.readOnly}
          placeholder={options.placeholder}
        />
      ) : (
        <p className="profile-field-value">{value}</p>
      )}
    </div>
  );

  return (
    <>
      {view === "MENU" ? (
        <button
          type="button"
          className="profile-overlay-backdrop"
          onClick={onBack}
          aria-label={t("Close")}
        />
      ) : null}
      <div className={`profile-page${view === "MENU" ? " is-overlay" : " is-detail"}`}>
        {view === "MENU" ? (
          <button
            type="button"
            className="profile-close-button"
            onClick={onBack}
            aria-label={t("Close")}
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        ) : null}
        <div className="profile-content">
        {view === "MENU" ? (
          <>
            <button
              type="button"
              className="profile-card-container"
              onClick={() => setView("EDIT")}
            >
              <div className="profile-card-box">
                <div className="profile-avatar-circle">
                  {profileImage ? (
                    <img
                      src={profileImage}
                      alt=""
                      className="avatar-image"
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <span
                    className="avatar-letter"
                    style={{ display: profileImage ? "none" : "flex" }}
                  >
                    {getInitial()}
                  </span>
                </div>
                <div className="profile-info">
                  <h2 className="profile-name">{fullName}</h2>
                  <p className="profile-hotel-info">
                    {t("Room {room} • {hotel}", { room: roomNumber, hotel: hotelName })}
                  </p>
                </div>
              </div>
            </button>

            <div className="profile-menu">
              <button className="profile-menu-row" onClick={() => setView("WALLET")}>
                <div className="profile-menu-icon" aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.8"/>
                    <path d="M3 10H21" stroke="currentColor" strokeWidth="1.8"/>
                    <path d="M16 14H18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                </div>
                <span className="profile-menu-label">{t("Payment Methods")}</span>
              </button>

              <button className="profile-menu-row" onClick={() => setView("SETTINGS")}>
                <div className="profile-menu-icon" aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8"/>
                    <path d="M12 3.5v2.2M12 18.3v2.2M3.5 12h2.2M18.3 12h2.2M5.8 5.8l1.6 1.6M16.6 16.6l1.6 1.6M18.2 5.8l-1.6 1.6M7.4 16.6l-1.6 1.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                </div>
                <span className="profile-menu-label">{t("Settings")}</span>
              </button>

              <div className="profile-menu-separator"></div>

              <button className="profile-menu-row" onClick={handleLogout}>
                <div className="profile-menu-icon" aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 4H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                    <path d="M10 12h9M16 8l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span className="profile-menu-label">{t("Logout")}</span>
              </button>
            </div>
          </>
        ) : (
          <div className="profile-detail">
            <div className="profile-detail-top">
              <button
                type="button"
                className="back-circle-btn"
                onClick={handleBackAction}
                aria-label={t("Back")}
              >
                <img
                  src={process.env.PUBLIC_URL + "/images/back_button.svg"}
                  alt=""
                />
              </button>
              <h1 className="profile-detail-title">
                <span className="title-phone">{isEditing ? t("Edit Profile") : t("Profile")}</span>
                <span className="title-wide">{t("Edit Profile")}</span>
              </h1>
              <button
                type="button"
                className={`profile-edit-btn${isEditing ? " is-save" : ""}`}
                onClick={isEditing ? handleSave : () => setIsEditing(true)}
                disabled={isSaving}
              >
                {isSaving ? "..." : isEditing ? t("Save") : t("Edit")}
              </button>
            </div>

            <div className="profile-detail-body">
              <div className="edit-avatar-section">
                <div
                  className={`edit-avatar-circle${isEditing ? " is-editing" : ""}`}
                  onClick={isEditing ? handleImageClick : undefined}
                  style={{ position: "relative" }}
                >
                  {profileImage ? (
                    <img
                      src={profileImage}
                      alt=""
                      className="avatar-image-large"
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <span
                    className="avatar-letter-large"
                    style={{ display: profileImage ? "none" : "flex" }}
                  >
                    {getInitial()}
                  </span>
                  {isUploadingImage && (
                    <div className="avatar-upload-overlay">
                      <div className="avatar-upload-spinner"></div>
                    </div>
                  )}
                  {isEditing && (
                    <div className="avatar-pencil-badge">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M18.5 2.5C18.8978 2.10218 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10218 21.5 2.5C21.8978 2.89782 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10218 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleFileSelect}
                />
              </div>

              <div className="profile-form-minimal">
                {renderProfileField(t("Full Name"), fullName, (e) => setFullName(e.target.value), {
                  placeholder: t("Enter your name"),
                })}
                {renderProfileField(t("Email Address"), email, (e) => setEmail(e.target.value), {
                  type: "email",
                  placeholder: t("Enter your email"),
                })}
                {renderProfileField(t("Phone Number"), phoneNumber, undefined, {
                  type: "tel",
                  readOnly: true,
                  placeholder: t("Enter your phone"),
                })}

                {saveMessage && (
                  <div
                    className={`save-status-minimal ${saveMessage === t("Profile updated successfully!") || saveMessage === t("Profile image updated successfully!") ? "success" : "error"}`}
                  >
                    {saveMessage}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </>
  );
};

export default ProfilePage;
